import { ref } from 'vue'
import { productService, type ExportArchiveStatus } from '@/services/productService'
import { toast } from '@/services/toastService'

// localStorage keys for export persistence
const EXPORT_ID_KEY = 'rukh_export_id'
const EXPORT_STATUS_KEY = 'rukh_export_status'

// Singleton state - shared across all instances
const exportId = ref<string | null>(null)
const exportStatus = ref<'queued' | 'processing' | 'completed' | 'failed' | null>(null)
const exportDownloadCompleted = ref<boolean>(false) // Track if download completed successfully
const exportWasInBackground = ref<boolean>(false) // Track if export was running in background
const downloadAttempted = ref<boolean>(false) // Track if download has been attempted to prevent duplicates
const backgroundNotificationShown = ref<boolean>(false) // Track if background notification has been shown
let exportStatusPollInterval: ReturnType<typeof setInterval> | null = null
let isInitialized = false
let tabVisibilityListener: (() => void) | null = null

/**
 * Global composable for managing export polling across all pages
 * This ensures exports continue to be monitored even when navigating away
 * Uses singleton pattern to ensure only one polling instance exists
 */
export function useExportPolling() {

  /**
   * Clear export state from localStorage and memory
   */
  const clearExportState = () => {
    localStorage.removeItem(EXPORT_ID_KEY)
    localStorage.removeItem(EXPORT_STATUS_KEY)
    exportId.value = null
    exportStatus.value = null
    exportDownloadCompleted.value = false
    exportWasInBackground.value = false
    downloadAttempted.value = false
    backgroundNotificationShown.value = false
    // Clear background flag and pending download from sessionStorage
    sessionStorage.removeItem('export_was_in_background')
    sessionStorage.removeItem('pending_export_download')
    sessionStorage.removeItem('download_attempted')
    sessionStorage.removeItem('background_notification_shown')
  }

  /**
   * Start polling for export status
   */
  const startExportStatusPolling = (id: string) => {
    // Stop any existing polling
    stopExportStatusPolling()

    exportId.value = id
    let pollCount = 0
    // Job timeout is 6 hours — keep polling slightly longer for status flush
    const MAX_POLLS = 10800 // 6 hours at 2-second intervals

    console.log('[Export Polling] Starting status polling for export:', id)
    
    // Set up tab visibility tracking to detect when export runs in background
    const handleTabVisibilityChange = () => {
      // Only track if export is actually in progress
      if (exportStatus.value === 'queued' || exportStatus.value === 'processing') {
        if (document.hidden || !document.hasFocus()) {
          // Tab became inactive during export
          if (!exportWasInBackground.value) {
            exportWasInBackground.value = true
            sessionStorage.setItem('export_was_in_background', 'true')
            console.log('[Export Polling] Tab became inactive - export running in background')
          }
        } else {
          // Tab became active - check if export was running in background
          if (exportWasInBackground.value || sessionStorage.getItem('export_was_in_background') === 'true') {
            const wasInBackground = exportWasInBackground.value || sessionStorage.getItem('export_was_in_background') === 'true'
            const notificationAlreadyShown = backgroundNotificationShown.value || sessionStorage.getItem('background_notification_shown') === 'true'
            
            if (wasInBackground && (exportStatus.value === 'queued' || exportStatus.value === 'processing') && !notificationAlreadyShown) {
              // Show welcome back notification (persistent until user closes it) - only once
              toast.info('📦 Your export continued running in the background while you were away. It will complete automatically.', {
                persistent: true
              })
              backgroundNotificationShown.value = true
              sessionStorage.setItem('background_notification_shown', 'true')
              console.log('[Export Polling] Tab became active - export was running in background')
            }
            
            // Reset flag since user is back (but keep notification flag to prevent duplicates)
            exportWasInBackground.value = false
            sessionStorage.removeItem('export_was_in_background')
          }
        }
      }
    }
    
    // Set up listeners for tab visibility
    document.addEventListener('visibilitychange', handleTabVisibilityChange)
    window.addEventListener('focus', handleTabVisibilityChange)
    window.addEventListener('blur', handleTabVisibilityChange)
    
    // Store listener for cleanup
    tabVisibilityListener = () => {
      document.removeEventListener('visibilitychange', handleTabVisibilityChange)
      window.removeEventListener('focus', handleTabVisibilityChange)
      window.removeEventListener('blur', handleTabVisibilityChange)
    }

    exportStatusPollInterval = setInterval(async () => {
      pollCount++

      // Safety limit: stop after 6 hours (allows for job timeout + buffer)
      if (pollCount > MAX_POLLS) {
        console.warn('[Export Polling] Polling timeout reached after 6 hours')
        stopExportStatusPolling()
        toast.error('Export is taking longer than expected. Please check back later or start a new export.')
        exportStatus.value = 'failed'
        clearExportState()
        return
      }

      try {
        const status = await productService.getExportArchiveStatus(id)

        exportStatus.value = status.status
        localStorage.setItem(EXPORT_STATUS_KEY, status.status)
        
        // Only log every 30 attempts (every minute) to reduce console noise
        if ((status.status === 'queued' || status.status === 'processing') && pollCount % 30 === 0) {
          console.log(`[Export Polling] Still processing... (checked ${pollCount} times, ~${Math.round(pollCount * 2 / 60)} minutes, status: ${status.status})`)
        }

        if (status.status === 'completed') {
          console.log('[Export Polling] Export completed successfully')
          console.log('[Export Polling] Status object:', {
            download_url: status.download_url,
            filename: status.filename,
            has_download_url: !!status.download_url,
            has_filename: !!status.filename,
            product_count: status.product_count,
            media_files_count: status.media_files_count,
            size_bytes: status.size_bytes
          })
          stopExportStatusPolling()
          
          // Clear background flag since export is complete
          exportWasInBackground.value = false
          sessionStorage.removeItem('export_was_in_background')

          // Check if download has already been attempted to prevent duplicates
          const alreadyAttempted = downloadAttempted.value || sessionStorage.getItem('download_attempted') === 'true'
          if (alreadyAttempted) {
            console.log('[Export Polling] Download already attempted, skipping duplicate attempt')
            return
          }

          // Download the file automatically (with a small delay to show the message first)
          if (status.download_url && status.filename) {
            try {
              // Mark download as attempted immediately to prevent duplicates
              downloadAttempted.value = true
              sessionStorage.setItem('download_attempted', 'true')
              
              // Check if tab is visible before attempting download
              const isTabVisible = !document.hidden && document.hasFocus()
              console.log('[Export Polling] Tab visibility check:', { hidden: document.hidden, hasFocus: document.hasFocus(), isTabVisible })
              
              if (!isTabVisible) {
                // Tab is inactive - store download info and set up listeners
                console.log('[Export Polling] Tab is inactive, storing download info for later')
                sessionStorage.setItem('pending_export_download', JSON.stringify({
                  url: status.download_url,
                  filename: status.filename,
                  product_count: status.product_count,
                  media_files_count: status.media_files_count
                }))
                
                // Show warning toast (do not promise automatic download)
                const warningMessage = status.product_count !== undefined && status.media_files_count !== undefined
                  ? `Export ready: ${status.product_count} products, ${status.media_files_count} media files. When you return to this tab you can download from the modal, or use the link in your email.`
                  : 'Export ready. When you return to this tab you can download from the modal, or use the link in your email.'
                toast.warning(warningMessage, { duration: 10000, persistent: true })
                
                // Set up listener for when tab becomes active
                const handleVisibilityChange = async () => {
                  if (!document.hidden && document.hasFocus()) {
                    console.log('[Export Polling] Tab became active, checking for pending download')
                    document.removeEventListener('visibilitychange', handleVisibilityChange)
                    window.removeEventListener('focus', handleVisibilityChange)
                    
                    // Check if download was already attempted
                    const alreadyAttempted = sessionStorage.getItem('download_attempted') === 'true'
                    if (alreadyAttempted) {
                      console.log('[Export Polling] Download already attempted, skipping')
                      sessionStorage.removeItem('pending_export_download')
                      return
                    }
                    
                    const pending = sessionStorage.getItem('pending_export_download')
                    if (pending) {
                      try {
                        // Mark as attempted before downloading
                        sessionStorage.setItem('download_attempted', 'true')
                        const { url, filename } = JSON.parse(pending)
                        console.log('[Export Polling] Attempting pending download:', { url, filename })
                        await productService.downloadExportArchive(url, filename)
                        sessionStorage.removeItem('pending_export_download')
                        
                        const successMessage = status.product_count !== undefined && status.media_files_count !== undefined
                          ? `Download started: ${status.product_count} products, ${status.media_files_count} media files`
                          : 'Download started successfully'
                        toast.success(successMessage)
                        
                        exportDownloadCompleted.value = true
                        setTimeout(() => {
                          clearExportState()
                          exportDownloadCompleted.value = false
                        }, 100)
                      } catch (e: any) {
                        console.error('[Export Polling] Auto-download on tab focus failed:', e)
                        
                        // Check if it's a 404 error - might mean file was already downloaded
                        const is404 = e?.message?.includes('404') || e?.response?.status === 404
                        if (is404) {
                          // File was likely already downloaded successfully, just clean up
                          console.log('[Export Polling] 404 error on tab focus - file likely already downloaded')
                          sessionStorage.removeItem('pending_export_download')
                          exportDownloadCompleted.value = true
                          setTimeout(() => {
                            clearExportState()
                            exportDownloadCompleted.value = false
                          }, 100)
                          return
                        }
                        
                        // For other errors, allow retry by clearing the attempted flag
                        sessionStorage.removeItem('download_attempted')
                        toast.error('Failed to start download. Please try again.')
                      }
                    }
                  }
                }
                
                document.addEventListener('visibilitychange', handleVisibilityChange)
                window.addEventListener('focus', handleVisibilityChange)
                
                // Do NOT open the API download URL in a new tab - that tab has no auth and returns 401.
                // Download will start when the user returns to the tab (visibility listener above)
                // or they can use the link in the completion email (frontend download page) which requires login.
                
                // Don't mark as completed yet - wait for download
                return
              }
              
              // Tab is active - proceed with normal download
              const completedMessage = status.product_count !== undefined && status.media_files_count !== undefined
                ? `Export ready: ${status.product_count} products, ${status.media_files_count} media files. You can download from the modal or use the link in your email.`
                : 'Export ready. You can download from the export modal or use the link in your email.'
              toast.success(completedMessage)
              
              await new Promise(resolve => setTimeout(resolve, 1000))
              
              console.log('[Export Polling] Attempting download:', {
                url: status.download_url,
                filename: status.filename
              })
              await productService.downloadExportArchive(status.download_url, status.filename)
              console.log('[Export Polling] Download function completed successfully')

              const successMessage = status.product_count !== undefined && status.media_files_count !== undefined
                ? `Download started: ${status.product_count} products, ${status.media_files_count} media files`
                : 'Download started successfully'
              toast.success(successMessage)
              
              // Mark download as completed - this will trigger modal reset in ProductsView
              exportDownloadCompleted.value = true
              
              // Small delay before clearing state to allow modal to react
              setTimeout(() => {
                clearExportState()
                // Reset the flag after clearing
                exportDownloadCompleted.value = false
              }, 100)
            } catch (downloadError: any) {
              console.error('[Export Polling] Download failed:', downloadError)
              console.error('[Export Polling] Download error details:', {
                message: downloadError?.message,
                stack: downloadError?.stack,
                name: downloadError?.name,
                response: downloadError?.response
              })
              
              // Check if it's a 404 error - might mean file was already downloaded and deleted
              const is404 = downloadError?.message?.includes('404') || downloadError?.response?.status === 404
              if (is404 && downloadAttempted.value) {
                // File was likely already downloaded successfully, just clean up
                console.log('[Export Polling] 404 error after download attempt - file likely already downloaded')
                exportDownloadCompleted.value = true
                setTimeout(() => {
                  clearExportState()
                  exportDownloadCompleted.value = false
                }, 100)
                return
              }
              
              // For other errors, allow retry by clearing the attempted flag
              downloadAttempted.value = false
              sessionStorage.removeItem('download_attempted')
              toast.error('Export completed but download failed. Please try again or contact support.')
              // Don't clear state on download failure - user might want to retry
            }
          } else {
            console.warn('[Export Polling] Export completed but missing download_url or filename:', {
              download_url: status.download_url,
              filename: status.filename
            })
            // No download URL - export completed but can't download, clear state anyway
            toast.error('Export completed but download URL is missing. Please contact support.')
            clearExportState()
          }
        } else if (status.status === 'failed') {
          console.error('[Export Polling] Export failed:', status.error || status.message)
          stopExportStatusPolling()
          toast.error(status.message || status.error || 'Export failed')
          // Clear background flag on failure
          exportWasInBackground.value = false
          sessionStorage.removeItem('export_was_in_background')
          clearExportState()
        }
      } catch (error: any) {
        console.error('[Export Polling] Error checking export status:', error)

        // Handle specific error cases
        if (error.response?.status === 404) {
          console.error('[Export Polling] Export not found (404)')
          stopExportStatusPolling()
          toast.error('Export not found or expired')
          clearExportState()
        } else if (error.response?.status === 403) {
          console.error('[Export Polling] Access denied (403)')
          stopExportStatusPolling()
          toast.error('You do not have access to this export')
          clearExportState()
        } else if (error.response?.status === 500) {
          // Continue polling on 500 errors (might be temporary)
          console.warn('[Export Polling] Server error (500) while checking export status, continuing to poll...')
        }
        // For other errors, continue polling (might be temporary network issue)
      }
    }, 2000) // Poll every 2 seconds
  }

  /**
   * Stop polling for export status
   */
  const stopExportStatusPolling = () => {
    if (exportStatusPollInterval) {
      clearInterval(exportStatusPollInterval)
      exportStatusPollInterval = null
    }
    
    // Clean up tab visibility listeners
    if (tabVisibilityListener) {
      tabVisibilityListener()
      tabVisibilityListener = null
    }
  }

  /**
   * Initialize export polling from localStorage
   * Call this on app mount to resume any in-progress exports
   * Also checks for pending downloads from inactive tabs
   */
  const initializeExportPolling = async () => {
    // Check for pending download from inactive tab
    const pendingDownload = sessionStorage.getItem('pending_export_download')
    const alreadyAttempted = sessionStorage.getItem('download_attempted') === 'true'
    
    if (pendingDownload && !document.hidden && document.hasFocus() && !alreadyAttempted) {
      try {
        console.log('[Export Polling] Found pending download, attempting to download now')
        // Mark as attempted before downloading
        sessionStorage.setItem('download_attempted', 'true')
        const { url, filename } = JSON.parse(pendingDownload)
        await productService.downloadExportArchive(url, filename)
        sessionStorage.removeItem('pending_export_download')
        toast.success('Download started successfully')
      } catch (e: any) {
        console.error('[Export Polling] Failed to process pending download:', e)
        
        // Check if it's a 404 error - might mean file was already downloaded
        const is404 = e?.message?.includes('404') || e?.response?.status === 404
        if (is404) {
          // File was likely already downloaded successfully, just clean up
          console.log('[Export Polling] 404 error on initialization - file likely already downloaded')
          sessionStorage.removeItem('pending_export_download')
          sessionStorage.removeItem('download_attempted')
          return
        }
        
        // For other errors, allow retry by clearing the attempted flag
        sessionStorage.removeItem('download_attempted')
        // Keep it in sessionStorage so user can retry
      }
    } else if (pendingDownload && alreadyAttempted) {
      // Download was already attempted, clean up
      console.log('[Export Polling] Pending download was already attempted, cleaning up')
      sessionStorage.removeItem('pending_export_download')
    }
    
    const savedExportId = localStorage.getItem(EXPORT_ID_KEY)
    if (savedExportId) {
      exportId.value = savedExportId
      const savedStatus = localStorage.getItem(EXPORT_STATUS_KEY)
      if (savedStatus && (savedStatus === 'queued' || savedStatus === 'processing')) {
        exportStatus.value = savedStatus as 'queued' | 'processing'
        
        // Check if export was running in background
        const wasInBackground = sessionStorage.getItem('export_was_in_background') === 'true'
        const notificationAlreadyShown = sessionStorage.getItem('background_notification_shown') === 'true'
        
        if (wasInBackground && !document.hidden && document.hasFocus() && !notificationAlreadyShown) {
          // Show welcome back notification (persistent until user closes it) - only once
          toast.info('📦 Your export continued running in the background while you were away. It will complete automatically.', {
            persistent: true
          })
          backgroundNotificationShown.value = true
          sessionStorage.setItem('background_notification_shown', 'true')
          sessionStorage.removeItem('export_was_in_background')
        } else if (wasInBackground && notificationAlreadyShown) {
          // Notification already shown, just clean up the background flag
          sessionStorage.removeItem('export_was_in_background')
        }
        
        // Resume polling - export will continue and download when complete
        startExportStatusPolling(savedExportId)
        console.log('[Export Polling] Resumed polling for export:', savedExportId, 'Status:', savedStatus)
      } else if (savedStatus === 'completed') {
        // Export completed but download might not have happened - clear stale data
        clearExportState()
      } else {
        // Clear stale export data
        clearExportState()
      }
    }
  }

  /**
   * Register a new export to be polled
   * Call this when starting a new export
   */
  const registerExport = (id: string, status: 'queued' | 'processing') => {
    exportId.value = id
    exportStatus.value = status
    localStorage.setItem(EXPORT_ID_KEY, id)
    localStorage.setItem(EXPORT_STATUS_KEY, status)
    startExportStatusPolling(id)
  }

  // Initialize only once (singleton pattern)
  if (!isInitialized) {
    initializeExportPolling()
    isInitialized = true
  }

  return {
    exportId,
    exportStatus,
    exportDownloadCompleted,
    exportWasInBackground,
    registerExport,
    stopExportStatusPolling,
    clearExportState,
    initializeExportPolling
  }
}

